/**
 * In-place patches for entity grid React Query caches (infinite pages or item arrays).
 * Used by SignalR so one changed row does not refetch every cached page.
 */

export function getCachedListRowId(row) {
    return Number(row?.id ?? row?.loadDetailId);
}

export function normalizeByIdsRows(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
}

export function toCrudOp(evt) {
    const op = String(evt?.operation ?? evt?.raw?.operation ?? evt?.Operation ?? '').toUpperCase().trim();
    if (op === 'I' || op === 'U' || op === 'D') return op;

    const action = String(evt?.action ?? evt?.Action ?? '').toUpperCase().trim();
    if (action === 'UPSERT' || action === 'UPDATE') return 'U';
    if (action === 'INSERT') return 'I';
    if (action === 'DELETE') return 'D';

    return null;
}

export function getByPath(obj, path) {
    if (obj == null || path == null || path === '') return undefined;
    return String(path).split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function compareSortValues(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function compareRows(a, b, sortBy, sortDir = 'asc') {
    const cmp = compareSortValues(getByPath(a, sortBy), getByPath(b, sortBy));
    if (cmp !== 0) return sortDir === 'desc' ? -cmp : cmp;
    return getCachedListRowId(a) - getCachedListRowId(b);
}

export function rowMatchesSearch(row, search) {
    const q = String(search ?? '').trim().toLowerCase();
    if (!q) return true;
    const fields = [
        row?.ref,
        row?.description,
        row?.erpCode,
        row?.name,
        row?.vehicleReg,
        row?.account?.name,
        row?.commodity?.name,
        row?.haulier?.name,
        row?.driver?.name,
    ];
    if (fields.some((value) => value != null && String(value).toLowerCase().includes(q))) {
        return true;
    }
    try {
        return JSON.stringify(row).toLowerCase().includes(q);
    } catch {
        return false;
    }
}

/** Calendar YYYY-MM-DD in local time, for list date-range params. */
export function toQueryDateString(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        return value.trim();
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function rowMatchesDateRange(row, startDate, endDate) {
    if (!startDate && !endDate) return true;
    const day = toQueryDateString(row?.transactionDate);
    if (!day) return true;
    if (startDate && day < startDate) return false;
    if (endDate && day > endDate) return false;
    return true;
}

export function inferListWindow(data) {
    if (Array.isArray(data)) {
        return { hasStart: true, hasEnd: true };
    }
    const pageParams = data?.pageParams;
    const hasStart = !Array.isArray(pageParams) || pageParams.length === 0 || pageParams[0] == null;
    const pages = data?.pages;
    const hasEnd = !Array.isArray(pages) || pages.length === 0
        ? true
        : pages[pages.length - 1]?.hasMore !== true;
    return { hasStart, hasEnd };
}

export function rowBelongsInLoadedWindow(row, items, { sortBy, sortDir = 'asc', hasStart = true, hasEnd = false } = {}) {
    if (!sortBy) return false;
    if (!Array.isArray(items) || items.length === 0) return hasStart;
    const vsFirst = compareRows(row, items[0], sortBy, sortDir);
    const vsLast = compareRows(row, items[items.length - 1], sortBy, sortDir);
    if (vsFirst < 0) return hasStart;
    if (vsLast > 0) return hasEnd;
    return true;
}

function patchItemsInPlace(items, { deleteSet, refreshedMap, askedSet }) {
    if (!Array.isArray(items) || items.length === 0) return items;

    let changed = false;
    const nextItems = [];

    for (const item of items) {
        const id = getCachedListRowId(item);

        if (deleteSet.has(id)) {
            changed = true;
            continue;
        }

        const updated = refreshedMap.get(id);
        if (updated) {
            changed = true;
            nextItems.push(updated);
            continue;
        }

        // Asked for a refresh but the row was not returned (disabled / filtered out).
        if (askedSet.has(id)) {
            changed = true;
            continue;
        }

        nextItems.push(item);
    }

    return changed ? nextItems : items;
}

function flattenPages(pages) {
    return (pages ?? []).flatMap((page) => page?.items ?? []);
}

function packIntoPages(oldPages, items) {
    if (!oldPages?.length) {
        return [{ items, hasMore: false }];
    }

    const nextPages = [];
    let offset = 0;
    for (let i = 0; i < oldPages.length; i += 1) {
        const isLast = i === oldPages.length - 1;
        const take = isLast ? items.length - offset : (oldPages[i].items?.length ?? 0);
        nextPages.push({
            ...oldPages[i],
            items: items.slice(offset, offset + Math.max(0, take)),
        });
        offset += Math.max(0, take);
        if (offset >= items.length && !isLast) {
            nextPages[nextPages.length - 1] = {
                ...nextPages[nextPages.length - 1],
                hasMore: oldPages[oldPages.length - 1]?.hasMore,
                cursor: oldPages[oldPages.length - 1]?.cursor,
            };
            break;
        }
    }
    return nextPages;
}

function mergeSortedListItems(items, {
    deleteSet,
    refreshedMap,
    askedSet,
    sortBy,
    sortDir,
    search,
    startDate,
    endDate,
    hasStart,
    hasEnd,
    hasActiveFilters,
    forceInsert = false,
}) {
    const loadedIds = new Set(items.map(getCachedListRowId).filter(Number.isFinite));
    const base = [];

    for (const item of items) {
        const id = getCachedListRowId(item);
        if (deleteSet.has(id) || askedSet.has(id) || refreshedMap.has(id)) continue;
        base.push(item);
    }

    const next = [...base];
    let changed = next.length !== items.length;

    for (const [id, row] of refreshedMap) {
        if (deleteSet.has(id)) continue;
        if (!rowMatchesSearch(row, search)) {
            changed = loadedIds.has(id) || changed;
            continue;
        }
        if (!forceInsert && !rowMatchesDateRange(row, startDate, endDate)) {
            changed = loadedIds.has(id) || changed;
            continue;
        }
        const wasLoaded = loadedIds.has(id);
        if (!wasLoaded && hasActiveFilters && !forceInsert) continue;
        if (forceInsert || rowBelongsInLoadedWindow(row, base, { sortBy, sortDir, hasStart, hasEnd })) {
            next.push(row);
            changed = true;
        } else if (wasLoaded) {
            changed = true;
        }
    }

    if (!changed) return items;
    next.sort((a, b) => compareRows(a, b, sortBy, sortDir));
    return next;
}

/**
 * Patch one cached list value. Supports infinite-query `{ pages }` and a plain row array.
 * When sortBy is set, unloaded rows that belong in the loaded sort window are inserted.
 */
export function patchCachedListData(old, {
    deleteIds = [],
    refreshedRows = [],
    askedIds = [],
    sortBy,
    sortDir = 'asc',
    search = '',
    startDate,
    endDate,
    hasStart,
    hasEnd,
    hasActiveFilters = false,
    forceInsert = false,
} = {}) {
    if (old == null) return old;

    const deleteSet = new Set((deleteIds ?? []).map(Number).filter(Number.isFinite));
    const askedSet = new Set((askedIds ?? []).map(Number).filter(Number.isFinite));
    const refreshedMap = new Map();
    for (const row of refreshedRows ?? []) {
        const id = getCachedListRowId(row);
        if (Number.isFinite(id)) refreshedMap.set(id, row);
    }

    const window = inferListWindow(old);
    const resolvedHasStart = hasStart ?? window.hasStart;
    const resolvedHasEnd = hasEnd ?? window.hasEnd;
    const canReorder = Boolean(sortBy);

    if (Array.isArray(old)) {
        if (!canReorder) {
            const next = patchItemsInPlace(old, { deleteSet, refreshedMap, askedSet });
            return next === old ? old : next;
        }
        const next = mergeSortedListItems(old, {
            deleteSet,
            refreshedMap,
            askedSet,
            sortBy,
            sortDir,
            search,
            startDate,
            endDate,
            hasStart: resolvedHasStart,
            hasEnd: resolvedHasEnd,
            hasActiveFilters,
            forceInsert,
        });
        return next === old ? old : next;
    }

    if (!Array.isArray(old.pages)) return old;

    if (!canReorder) {
        let anyPageChanged = false;
        const nextPages = old.pages.map((page) => {
            const items = page?.items ?? [];
            const nextItems = patchItemsInPlace(items, { deleteSet, refreshedMap, askedSet });
            if (nextItems === items) return page;
            anyPageChanged = true;
            return { ...page, items: nextItems };
        });
        return anyPageChanged ? { ...old, pages: nextPages } : old;
    }

    const flat = flattenPages(old.pages);
    const nextItems = mergeSortedListItems(flat, {
        deleteSet,
        refreshedMap,
        askedSet,
        sortBy,
        sortDir,
        search,
        startDate,
        endDate,
        hasStart: resolvedHasStart,
        hasEnd: resolvedHasEnd,
        hasActiveFilters,
        forceInsert,
    });
    if (nextItems === flat) return old;
    return { ...old, pages: packIntoPages(old.pages, nextItems) };
}

export function collectLoadedIdsFromData(data, ids = new Set()) {
    if (!data) return ids;

    const addRow = (row) => {
        const id = getCachedListRowId(row);
        if (Number.isFinite(id)) ids.add(id);
    };

    if (Array.isArray(data)) {
        data.forEach(addRow);
        return ids;
    }

    if (Array.isArray(data.pages)) {
        for (const page of data.pages) {
            (page?.items ?? []).forEach(addRow);
        }
    }

    return ids;
}

export function collectLoadedIds(queryClient, listQueryKey) {
    const ids = new Set();
    if (!queryClient || !listQueryKey) return ids;

    const queries = queryClient.getQueryCache().findAll({
        queryKey: [listQueryKey],
        exact: false,
    });

    for (const query of queries) {
        collectLoadedIdsFromData(query.state?.data, ids);
    }

    return ids;
}

function listQueryMeta(queryKey, data) {
    const params = queryKey?.[1];
    const filterItems = params?.filter?.items ?? params?.filterModel?.items;
    return {
        sortBy: params?.sortBy,
        sortDir: params?.sortDir ?? 'asc',
        search: params?.search ?? '',
        startDate: params?.startDate ?? null,
        endDate: params?.endDate ?? null,
        hasActiveFilters: Array.isArray(filterItems) && filterItems.length > 0,
        ...inferListWindow(data),
    };
}

export function findCachedListRow(queryClient, listQueryKey, rowId) {
    const want = Number(rowId);
    if (!queryClient || !listQueryKey || !Number.isFinite(want)) return null;

    const queries = queryClient.getQueryCache().findAll({
        queryKey: [listQueryKey],
        exact: false,
    });

    for (const query of queries) {
        const data = query.state?.data;
        const items = Array.isArray(data)
            ? data
            : (data?.pages ?? []).flatMap((page) => page?.items ?? []);
        const row = items.find((item) => getCachedListRowId(item) === want);
        if (row) return row;
    }

    return null;
}

export function applyListRowDeltaToQueryClient(queryClient, listQueryKey, delta) {
    if (!queryClient || !listQueryKey) return false;

    const queries = queryClient.getQueryCache().findAll({
        queryKey: [listQueryKey],
        exact: false,
    });

    let changed = false;
    for (const query of queries) {
        const meta = listQueryMeta(query.queryKey, query.state?.data);
        const old = query.state?.data;
        const next = patchCachedListData(old, { ...delta, ...meta });
        if (next === old) continue;
        queryClient.setQueryData(query.queryKey, next);
        changed = true;
    }
    return changed;
}
